import { useState, useEffect } from "react"
import { Info } from "lucide-react"
import { get, post } from "../../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../../common/pagination"
import PopupPracticeSelector from "./practicesAssociationTabs/popupPracticeSelector"

const FaxRowSub = ({
  user,
  saveLoading,
  handleAssign,
  selectOptions,
  authUser,
  hoveredRow,
  activeRow,
  setHoveredRow,
  setActiveRow,
}) => {
  const selectedValues =
    user?.association?.subFolderNames?.map((subFolder) => ({
      value: subFolder,
      label: subFolder,
    })) || []

  const selectedValuesString = user?.association?.subFolderNames?.join(", ")

  return (
    <tr
      className={`${hoveredRow === user.emailAddress || activeRow === user.emailAddress ? "row-highlighted" : ""}`}
      onMouseEnter={() => {
        setHoveredRow(user.emailAddress)
        if (activeRow !== user.emailAddress) setActiveRow(null)
      }}
      onMouseLeave={() => setHoveredRow(null)}
    >
      <td className="faxes-td" style={{ width: "200px" }}>
        {user.emailAddress}
      </td>
      <td
        className="faxes-td"
        style={{
          width: "200px",
          maxWidth: "100px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={selectedValuesString}
      >
        {selectedValuesString}
      </td>
      <td className="faxes-td" style={{ width: "200px" }}>
        {user?.association?.onlySubfolderAssociation ? "Yes" : "No"}
      </td>
      <td className="faxes-td" style={{ textAlign: "center" }}>
        {saveLoading === user.emailAddress ? (
          "Saving ..."
        ) : (
          <PopupPracticeSelector
            key={user.emailAddress}
            emailAddress={user.emailAddress}
            selectOptions={selectOptions}
            selectedValues={selectedValues}
            onApply={(selectedOptions, onlySubfolderAssociationState) => {
              handleAssign(user, selectedOptions, onlySubfolderAssociationState)
            }}
            linkLabel="Assign"
            title={`Assign Subfolder to ${user.emailAddress}`}
            saveLoading={saveLoading}
            authUser={authUser}
            onlySubfolderAssociation={{
              selectedValue: user?.association?.onlySubfolderAssociation,
            }}
            type="subfolderAssociation"
          />
        )}
      </td>
    </tr>
  )
}

const SubfolderAssociation = ({
  authUser,
  api,
  tab,
  practices,
  setSelectedPractice,
  selectedPractice,
}) => {
  let [users, setUsers] = useState([])
  const [appLoading] = useState(false)
  const [userLoading, setUserLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  // const selectOptions = useMemo(
  //   () =>
  //     selectedPractice?.subFolders?.map((subFolder) => ({
  //       value: subFolder,
  //       label: subFolder,
  //     })),
  //   [selectedPractice.subFolders]
  // )

  const selectOptions =
    selectedPractice?.subFolders?.map((subFolder) => ({
      value: subFolder,
      label: subFolder,
    })) || []

  // Search Filter
  const filteredUsers = users?.filter((faxNumber) => {
    return Object.entries(faxNumber).some(([key, value]) => {
      if (key === "emailAddress") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase().trim())
      }
      return false
    })
  })

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentUsers = filteredUsers.slice(startIdx, startIdx + rowsPerPage)
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage)

  const fetchUsersByPracticeId = async (practiceId) => {
    try {
      // GET ALL USERS
      const data = await get(`/practice/userPracticeAssociations/${practiceId}`)
      setUsers(data)
    } catch (error) {
      console.error("Failed to fetch apps", error)
    } finally {
    }
  }

  const loadUsersByPracticeId = async (practiceId) => {
    setUserLoading(true)
    await fetchUsersByPracticeId(practiceId)
    setUserLoading(false)
  }

  useEffect(() => {
    // Set subfolder
    if (selectedPractice?.practiceId) {
      loadUsersByPracticeId(selectedPractice.practiceId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPractice])

  const handleAppChange = (event) => {
    const practiceId = event.target.value
    const onSelect = practices.find((app) => app.practiceId === practiceId)

    setSelectedPractice(onSelect)
  }

  const handleAssign = async (
    user,
    selectedOptions,
    onlySubfolderAssociationState,
  ) => {
    //Prepare New user numbers
    if (!selectedOptions) return
    setSaveLoading(user.emailAddress)

    const subfolders = selectedOptions.map((d) => d.value)

    try {
      const responseAssign = await post(
        "/practice/associate-user-practice-subfolders",
        {
          emailAddress: user.emailAddress,
          association: {
            practiceId: selectedPractice.practiceId,
            subFolderNames: subfolders,
            onlySubfolderAssociation: onlySubfolderAssociationState,
          },
        },
      )

      toast.success(responseAssign.message, {
        duration: 5000,
      })

      fetchUsersByPracticeId(selectedPractice.practiceId)
    } catch (error) {
      console.error("Failed to assign users", error)
      toast.error(error, {
        duration: 5000,
      })
    } finally {
      setSaveLoading(null)
    }
  }

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {appLoading ? (
        <div
          className="loading-container-user-list"
          style={{ minHeight: "500px" }}
        >
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <>
          <div
            className="faxes-content"
            style={{
              textAlign: "center",
              width: "100%",
              marginTop: "20px",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              className="search-container"
              style={{
                width: "100%",
                alignItems: "end",
                display: "flex",
                justifyContent: "space-between",
                boxSizing: "border-box",
                paddingRight: "5px",
              }}
            >
              <div className="setting-note">
                <select
                  className="select-dropdown"
                  style={{ width: "200px" }}
                  onChange={handleAppChange}
                  value={selectedPractice.practiceId || ""}
                >
                  {practices.map((practice) => (
                    <option
                      key={practice.practiceId}
                      value={practice.practiceId}
                    >
                      {practice.practiceName}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flexShrink: 0 }}>
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder={`Search User`}
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value)
                      setCurrentPage(1)
                    }}
                    style={{ width: "200px", paddingRight: "22px" }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearchText("")
                      setCurrentPage(1)
                    }}
                    className="search-clear-btn"
                    style={{ visibility: searchText ? "visible" : "hidden" }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
            {userLoading ? (
              <div className="loading-container-user-list">
                <div className="loading-message">Loading...</div>
              </div>
            ) : (
              <>
                <>
                  <div className="table-container">
                    <table className="faxes-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="faxes-th" style={{ width: "25%" }}>
                            {`${tab === "admin" ? "Admin" : "User"}`}
                          </th>
                          <th className="faxes-th" style={{ width: "45%" }}>
                            Subfolders
                          </th>
                          <th className="faxes-th" style={{ width: "20%" }}>
                            <span
                              title={`'Only Subfolders' allows the user to access only assigned subfolders within a practice. User cannot access faxes available in main practice. Use this for limited access control.`}
                            >
                              <Info
                                size={16}
                                style={{
                                  verticalAlign: "middle",
                                  cursor: "help",
                                  marginRight: "4px",
                                }}
                              />
                            </span>
                            Only Subfolders
                          </th>
                          <th
                            className="faxes-th"
                            style={{ textAlign: "center" }}
                          >
                            Action
                          </th>
                          {/* <th
                              className="faxes-th"
                              style={{ textAlign: "center" }}
                            >
                              Action
                            </th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {currentUsers.length > 0 ? (
                          currentUsers.map((user, index) => (
                            <FaxRowSub
                              key={`${user.emailAddress}-${index}`}
                              user={user}
                              saveLoading={saveLoading}
                              handleAssign={handleAssign}
                              selectOptions={selectOptions}
                              authUser={authUser}
                              hoveredRow={hoveredRow}
                              activeRow={activeRow}
                              setHoveredRow={setHoveredRow}
                              setActiveRow={setActiveRow}
                            />
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="100%"
                              style={{
                                textAlign: "center",
                                padding: "10px",
                                border: "1px solid #ccc",
                              }}
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredUsers.length > 0 && (
                    <div
                      className="faxes-pagination"
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      {filteredUsers.length > 0 && (
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          rowsPerPage={rowsPerPage}
                          totalRows={filteredUsers.length}
                          onPageChange={(p) => setCurrentPage(p)}
                          onRowsPerPageChange={(count) => {
                            setRowsPerPage(count)
                            setCurrentPage(1) // Reset to first page on change
                          }}
                        />
                      )}
                    </div>
                  )}
                </>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default SubfolderAssociation
